import type {
	PermissionTree,
	RoleDetail,
	RoleListResponse,
	CreateRolePayload,
	CreateRoleResponse,
	UpdateRolePayload,
} from '../types/role.types';
import type { CursorParams } from '../types/api.types';
import { api } from './client';

/**
 * Query key factory for roles
 */
export const roleKeys = {
	all: ['roles'] as const,
	lists: () => [...roleKeys.all, 'list'] as const,
	list: (params?: Record<string, unknown>) => [...roleKeys.lists(), params] as const,
	details: () => [...roleKeys.all, 'detail'] as const,
	detail: (id: string) => [...roleKeys.details(), id] as const,
	permissionTree: () => [...roleKeys.all, 'permissionTree'] as const,
};

/**
 * Fetch paginated list of roles
 */
export async function getRoles(params?: CursorParams): Promise<RoleListResponse> {
	const response = await api.get<RoleListResponse>('/v1/iam/roles', { params });
	return response.data;
}

/**
 * Fetch the permission tree
 */
export async function getPermissionTree(): Promise<PermissionTree[]> {
	const response = await api.get<PermissionTree[]>('/v1/iam/roles/permissions/tree');
	return response.data;
}

/**
 * Create a new role
 */
export async function createRole(payload: CreateRolePayload): Promise<CreateRoleResponse> {
	const response = await api.post<CreateRoleResponse>('/v1/iam/roles', payload);
	return response.data;
}

/**
 * Fetch a single role by ID
 */
export async function getRole(id: string): Promise<RoleDetail> {
	const response = await api.get<RoleDetail>(`/v1/iam/roles/${id}`);
	return response.data;
}

/**
 * Update an existing role
 */
export async function updateRole(id: string, payload: UpdateRolePayload): Promise<RoleDetail> {
	const response = await api.put<RoleDetail>(`/v1/iam/roles/${id}`, payload);
	return response.data;
}
